import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Tabs, TabsList, TabsContent } from '../../components/ui/tabs';
import { TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { TestChat, ConversationAnalytics, UnansweredQuestions, SyncProgress, SetupChecklist, QuickActionsCard } from '../../features/domains/components';
import { useDomain, useUpdateDomain } from '../../features/domains/hooks/useDomains';
import { ROUTES, getDomainDiscoverPath } from '../../config/routes';
import { WIDGET_URL } from '../../config/constants';
import { generateEmbedCode, copyToClipboard, formatDate } from '../../lib/utils';
import { getDomainDisplayStatus } from '../../lib/domainStatus';
import { usePageTitle } from '../../hooks/usePageTitle';
import { InstallGuideModal } from '../../features/domains/components/InstallGuideModal';
import { useConversationAnalytics } from '../../features/domains/hooks/useConversationAnalytics';
import { ChatHistoryPage } from './ChatHistoryPage';
import { OverageSettings } from '../../features/domains/components/OverageSettings';

function relativeTime(dateStr: string | null): string {
  if (!dateStr) return '—';
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffHours = Math.floor(diffMs / 3600000);
  if (diffHours < 1) return `${Math.floor(diffMs / 60000)}分前`;
  if (diffHours < 24) return `${diffHours}時間前`;
  return `${Math.floor(diffHours / 24)}日前`;
}

export const DomainDetailPage: React.FC = () => {
  usePageTitle('BOT詳細');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS = ['overview', 'settings', 'analytics', 'history'];
  const tabParam = searchParams.get('tab') || 'overview';
  const activeTab = VALID_TABS.includes(tabParam) ? tabParam : 'overview';
  const setActiveTab = (tab: string) => {
    navigate({ search: `?tab=${tab}`, hash: '' }, { replace: true });
  };

  useEffect(() => {
    // 旧タブURLリダイレクト: tab=files→overview, tab=webhooks/embed→settings, tab=chat→analytics#test-chat
    if (tabParam === 'files') {
      setSearchParams({ tab: 'overview' }, { replace: true });
    } else if (tabParam === 'webhooks' || tabParam === 'embed') {
      setSearchParams({ tab: 'settings' }, { replace: true });
    } else if (tabParam === 'chat') {
      navigate({ search: '?tab=overview' }, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabParam]);

  useEffect(() => {
    if (location.hash === '#test-chat') {
      setTimeout(() => {
        document.getElementById('test-chat')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [location.hash]);

  const [embedCopied, setEmbedCopied] = useState(false);
  const [installGuideOpen, setInstallGuideOpen] = useState(false);
  const [trendDays, setTrendDays] = useState<7 | 30>(7);
  const [savingField, setSavingField] = React.useState<string | null>(null);

  const { data, isLoading, error } = useDomain(id || '');
  const updateDomain = useUpdateDomain(id || '');
  const { data: analyticsData } = useConversationAnalytics(id || '', undefined, undefined);
  const summary = analyticsData?.data?.summary;
  const dailyTrend = analyticsData?.data?.daily_trend ?? [];

  const domain = data?.data;

  const pdLevel = useMemo(() => {
    const syncStatus = domain?.sync_status;
    const apiKeyMasked = domain?.api_key_masked;
    if ((syncStatus === 'synced' || syncStatus === 'error') && apiKeyMasked) return 2;
    if (syncStatus === 'synced' || syncStatus === 'error') return 1;
    return 0;
  }, [domain?.sync_status, domain?.api_key_masked]);

  if (isLoading) {
    return (
      <div data-testid="domain-detail-skeleton">
        <div className="animate-pulse bg-gray-200 rounded-lg h-12 w-full mb-2" />
        <div className="animate-pulse bg-gray-200 rounded-lg h-96" />
      </div>
    );
  }

  if (error || !domain) {
    return (
      <div data-testid="not-found-page" className="text-center py-8">
        <p className="text-text-secondary text-lg">BOTが見つかりません</p>
        <Button
          variant="outline"
          onClick={() => navigate(ROUTES.DOMAINS)}
          className="mt-4"
        >
          チャットBOT一覧に戻る
        </Button>
      </div>
    );
  }

  const handleEmbedCopy = async () => {
    const code = generateEmbedCode(domain.api_key || domain.api_key_masked || '');
    const success = await copyToClipboard(code);
    if (success) {
      setEmbedCopied(true);
      setTimeout(() => setEmbedCopied(false), 2000);
    }
  };

  const saveSettings = (patch: Parameters<typeof updateDomain.mutate>[0], field: string) => {
    setSavingField(field);
    updateDomain.mutate(patch, { onSettled: () => setSavingField(null) });
  };

  return (
    <div data-testid="domain-detail-page">
      {/* 戻り導線 */}
      <button
        onClick={() => navigate(ROUTES.DOMAINS)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#64748B', background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 12px 0' }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" x2="5" y1="12" y2="12"/><polyline points="12 19 5 12 12 5"/>
        </svg>
        チャットボット
      </button>
      {/* page-head */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="min-w-0 flex-1">
          <h1
            title={domain.description || domain.domain}
            className="text-2xl font-bold text-text-primary truncate"
          >
            {domain.description || domain.domain}
          </h1>
          <p
            title={domain.domain}
            className="text-sm text-text-secondary mt-0.5 truncate"
          >
            {domain.domain}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {(() => {
            const displayStatus = getDomainDisplayStatus(domain.status, domain.sync_status);
            if (displayStatus === 'active') return <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, background: '#DCFCE7', color: '#166534' }}>稼働中</span>;
            if (displayStatus === 'learning') return <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, background: '#FEF3C7', color: '#92400E' }}>学習中…</span>;
            if (displayStatus === 'inactive') return <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, background: '#F1F5F9', color: '#64748B' }}>停止中</span>;
            return <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, background: '#FEE2E2', color: '#991B1B' }}>エラー</span>;
          })()}
        </div>
      </div>

      <SyncProgress
        domainId={domain.id}
        syncStatus={domain.sync_status}
        filesTotal={domain.files_total}
        filesProcessed={domain.files_processed}
        onRelearn={() => navigate(getDomainDiscoverPath(domain.id))}
        errorCount={domain.crawl_files?.filter((f: any) => f.sync_status === 'error').length}
        syncedCount={domain.crawl_files?.filter((f: any) => f.sync_status === 'synced').length}
      />

      {/* タブ */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto">
          <TabsList className="mb-4">
            <TabsTrigger value="overview" data-testid="domain-detail-tab-overview">概要</TabsTrigger>
            {pdLevel >= 1 && <TabsTrigger value="settings" data-testid="domain-detail-tab-settings">設定</TabsTrigger>}
            {pdLevel >= 2 && <TabsTrigger value="analytics" data-testid="tab-analytics">分析</TabsTrigger>}
            {pdLevel >= 2 && <TabsTrigger value="history" data-testid="tab-history">会話履歴</TabsTrigger>}
          </TabsList>
        </div>

        {/* 概要タブ */}
        <TabsContent value="overview">
          {pdLevel === 0 ? (
            /* pd0: 学習中状態 */
            <div className="flex flex-col gap-4">
              {/* ボット情報 + セットアップ */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* ボット情報 */}
                <div className="bg-white border border-border rounded-xl p-6">
                  <h3 className="text-base font-bold text-text-primary mb-4">ボット情報</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-text-secondary">学習ページ数</span>
                      <span className="font-medium text-text-primary">{domain.files_processed} ページ</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-text-secondary">ファイル総数</span>
                      <span className="font-medium text-text-primary">{domain.files_total} ファイル</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-text-secondary">作成日</span>
                      <span className="font-medium text-text-primary">{formatDate(domain.created_at)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-text-secondary">最終学習</span>
                      <span className="font-medium text-text-primary">{relativeTime(domain.last_sync_at)}</span>
                    </div>
                  </div>
                </div>

                {/* セットアップ */}
                <SetupChecklist
                  filesTotalCount={domain.files_total}
                  syncStatus={domain.sync_status}
                  apiKeyMasked={domain.api_key_masked}
                  onTabChange={setActiveTab}
                  domainId={domain.id}
                />
              </div>

              {/* 学習ファイル */}
              {domain.crawl_files && domain.crawl_files.length > 0 && (
                <div className="bg-white border border-border rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-bold text-text-primary">学習ファイル</h3>
                    <button
                      type="button"
                      onClick={() => navigate(getDomainDiscoverPath(domain.id))}
                      className="text-sm font-bold hover:underline"
                      style={{ color: '#0a7a72', background: 'none', border: 'none', cursor: 'pointer' }}
                    >
                      再学習
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left text-text-secondary font-medium pb-2 pr-4">ページ</th>
                          <th className="text-left text-text-secondary font-medium pb-2 pr-4">URL</th>
                          <th className="text-left text-text-secondary font-medium pb-2 pr-4">ステータス</th>
                          <th className="text-left text-text-secondary font-medium pb-2">更新</th>
                        </tr>
                      </thead>
                      <tbody>
                        {domain.crawl_files.slice(0, 10).map((file, i) => (
                          <tr key={i} className="border-b border-border last:border-b-0">
                            <td className="py-3 pr-4 text-text-primary max-w-[120px] truncate">{file.original_filename || '—'}</td>
                            <td className="py-3 pr-4 text-text-secondary max-w-[160px] truncate">{file.url || '—'}</td>
                            <td className="py-3 pr-4">
                              {file.sync_status === 'synced' ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#DCFCE7] text-[#166534]">学習済み</span>
                              ) : file.sync_status === 'error' ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">エラー</span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#FEF3C7] text-[#92400E]">処理中</span>
                              )}
                            </td>
                            <td className="py-3 text-text-secondary">—</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* pd1+: 学習完了状態 */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

              {/* 動作をためす（TestChat実体） */}
              <div id="test-chat" style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '24px' }}>
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#0F172A', marginBottom: '4px' }}>動作をためす</div>
                <div style={{ fontSize: '13px', color: '#64748B', marginBottom: '16px' }}>お客様がどう見えるか、チャットで確認できます。</div>
                <TestChat domainId={domain.id} kbSyncStatus={domain.sync_status} />
              </div>

              {/* 埋め込みコード */}
              <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '24px' }}>
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#0F172A', marginBottom: '16px' }}>埋め込みコード</div>
                <div style={{ position: 'relative', background: '#0F172A', borderRadius: '10px', padding: '20px 20px 20px 24px', fontFamily: 'monospace', fontSize: '13px', lineHeight: '1.7', overflowX: 'auto' }}>
                  <button
                    onClick={handleEmbedCopy}
                    style={{ position: 'absolute', top: '12px', right: '12px', background: embedCopied ? '#16A34A' : '#1E293B', color: embedCopied ? '#fff' : '#94A3B8', border: '1px solid #334155', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}
                  >
                    {embedCopied ? (
                      <>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                        コピー済み
                      </>
                    ) : (
                      <>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                        コピー
                      </>
                    )}
                  </button>
                  <pre data-testid="embed-code-snippet" style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    <span style={{ color: '#38BDF8' }}>&lt;script </span>
                    <span style={{ color: '#FBBF24' }}>src</span>
                    <span style={{ color: '#38BDF8' }}>=</span>
                    <span style={{ color: '#86EFAC' }}>"{WIDGET_URL}/widget.js"</span>
                    <span style={{ color: '#38BDF8' }}>&gt;&lt;/script&gt;</span>
                    {'\n'}
                    <span style={{ color: '#38BDF8' }}>&lt;script&gt;</span>
                    {'\n  '}
                    <span style={{ color: '#E2E8F0' }}>EntryBot.init({'{'}</span>
                    {'\n    '}
                    <span style={{ color: '#FBBF24' }}>apiKey</span>
                    <span style={{ color: '#E2E8F0' }}>: </span>
                    <span style={{ color: '#86EFAC' }}>'{domain.api_key || domain.api_key_masked || ''}'</span>
                    {'\n  '}
                    <span style={{ color: '#E2E8F0' }}>{'}'})</span>
                    <span style={{ color: '#94A3B8' }}>;</span>
                    {'\n'}
                    <span style={{ color: '#38BDF8' }}>&lt;/script&gt;</span>
                  </pre>
                </div>
                <div data-testid="embed-csp-guide" style={{ marginTop: '12px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '12px 16px', fontSize: '12px', color: '#475569' }}>
                  <div style={{ fontWeight: 600, marginBottom: '6px', color: '#334155' }}>埋め込み先サイトのCSPに以下を許可してください</div>
                  <code style={{ display: 'block', fontFamily: 'monospace', lineHeight: '1.8' }}>
                    {`script-src https://${new URL(WIDGET_URL).host}`}<br />
                    {`connect-src https://${new URL(WIDGET_URL).host}`}
                  </code>
                </div>
                <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#0F172A', marginBottom: '4px' }}>貼り付け方がわからない場合</div>
                    <div style={{ fontSize: '13px', color: '#64748B' }}>ヘルプページで埋め込み手順をご確認いただけます。</div>
                  </div>
                  <button
                    onClick={() => setInstallGuideOpen(true)}
                    style={{ background: '#fff', color: '#0a7a72', border: '1px solid #0a7a72', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}
                    data-testid="paste-guide-button"
                  >
                    貼り付け方を見る
                  </button>
                </div>
              </div>

              {/* クイックアクション */}
              <QuickActionsCard
                onTabChange={setActiveTab}
                domainId={domain.id}
                onAddLearning={() => navigate(getDomainDiscoverPath(domain.id))}
                onCopyEmbed={() => copyToClipboard(generateEmbedCode(domain.api_key || domain.api_key_masked || ''))}
              />

            </div>
          )}
        </TabsContent>

        {/* 設定タブ */}
        <TabsContent value="settings">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '640px' }}>

            {/* カード①: 基本設定 */}
            <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '24px' }}>
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#0F172A', marginBottom: '18px' }}>基本設定</div>
              {/* チャットボットの名前 */}
              <div style={{ marginBottom: '20px', opacity: savingField === 'bot_name' ? 0.6 : 1, pointerEvents: savingField === 'bot_name' ? 'none' : 'auto' }}>
                <label style={{ fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  チャットボットの名前
                  {savingField === 'bot_name' && (
                    <span data-testid="settings-saving-indicator" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#0a7a72', fontWeight: 400 }}>
                      <Loader2 className="h-3 w-3 animate-spin" />保存中…
                    </span>
                  )}
                </label>
                <div style={{ border: '1px solid #E2E8F0', borderRadius: '8px', overflow: 'hidden' }}>
                  <input
                    key={domain.settings?.bot_name}
                    defaultValue={domain.settings?.bot_name || ''}
                    onBlur={(e) => { if (e.target.value !== domain.settings?.bot_name) saveSettings({ settings: { bot_name: e.target.value } }, 'bot_name'); }}
                    style={{ width: '100%', fontSize: '14px', padding: '11px 14px', border: 'none', outline: 'none', fontFamily: 'inherit', color: '#0F172A', background: '#fff' }}
                  />
                </div>
              </div>
              {/* 応答のトーン */}
              <div style={{ opacity: savingField === 'tone' ? 0.6 : 1, pointerEvents: savingField === 'tone' ? 'none' : 'auto' }}>
                <label style={{ fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  応答のトーン
                  {savingField === 'tone' && (
                    <span data-testid="settings-saving-indicator" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#0a7a72', fontWeight: 400 }}>
                      <Loader2 className="h-3 w-3 animate-spin" />保存中…
                    </span>
                  )}
                </label>
                <div style={{ display: 'flex', gap: '12px' }}>
                  {(['polite', 'friendly'] as const).map((t) => {
                    const selected = (domain.settings?.tone || 'polite') === t;
                    return (
                      <div
                        key={t}
                        onClick={() => saveSettings({ settings: { tone: t } }, 'tone')}
                        style={{
                          flex: 1, borderRadius: '10px', padding: '14px', cursor: 'pointer',
                          border: selected ? '2px solid #0a7a72' : '1px solid #E2E8F0',
                          background: selected ? '#F0FDFA' : '#fff',
                        }}
                      >
                        <div style={{ fontSize: '13px', fontWeight: 700, color: selected ? '#0F766E' : '#334155', marginBottom: '4px' }}>
                          {t === 'polite' ? 'ていねい' : 'フレンドリー'}
                        </div>
                        <div style={{ fontSize: '12px', color: selected ? '#64748B' : '#94A3B8' }}>
                          {t === 'polite' ? '丁寧な敬語で応対します' : '親しみやすい口調で応対します'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* カード②: リードの収集 */}
            <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* トグル行 */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: '#0F172A', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    リードの収集
                    {savingField === 'lead_collection_enabled' && (
                      <span data-testid="settings-saving-indicator" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#0a7a72', fontWeight: 400 }}>
                        <Loader2 className="h-3 w-3 animate-spin" />保存中…
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '13px', color: '#64748B' }}>
                    会話の中で、お客様の名前や連絡先を集めます。{' '}
                    <span style={{ color: '#B45309', fontWeight: 700 }}>集めた内容の閲覧はProプランから</span>
                  </div>
                </div>
                <div
                  onClick={() => saveSettings({ settings: { lead_collection_enabled: !domain.settings?.lead_collection_enabled } }, 'lead_collection_enabled')}
                  style={{ width: '44px', height: '24px', borderRadius: '12px', background: domain.settings?.lead_collection_enabled ? '#0a7a72' : '#E2E8F0', cursor: 'pointer', opacity: savingField === 'lead_collection_enabled' ? 0.6 : 1, position: 'relative', transition: 'background 0.2s', flexShrink: 0, pointerEvents: savingField === 'lead_collection_enabled' ? 'none' : 'auto' }}
                >
                  <div style={{ position: 'absolute', top: '3px', left: domain.settings?.lead_collection_enabled ? '23px' : '3px', width: '18px', height: '18px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                </div>
              </div>
              {/* N回入力行 */}
              <div style={{ opacity: !domain.settings?.lead_collection_enabled ? 0.5 : 1, pointerEvents: !domain.settings?.lead_collection_enabled ? 'none' : 'auto' }}>
                <label style={{ fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  後出しトリガー（N回返答後に表示・既定3）
                  {savingField === 'lead_trigger_after_replies' && (
                    <span data-testid="settings-saving-indicator" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#0a7a72', fontWeight: 400 }}>
                      <Loader2 className="h-3 w-3 animate-spin" />保存中…
                    </span>
                  )}
                </label>
                <div style={{ fontSize: '12px', color: '#64748B', marginBottom: '8px' }}>
                  ボットがN回返答した後、会話を中断せずフォームをインライン表示します（1〜10）
                </div>
                <input
                  type="number"
                  min={1}
                  max={10}
                  key={domain.settings?.lead_trigger_after_replies}
                  data-testid="lead-trigger-after-replies-input"
                  defaultValue={domain.settings?.lead_trigger_after_replies ?? 3}
                  onBlur={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val)) {
                      const clamped = Math.min(10, Math.max(1, val));
                      saveSettings({ settings: { lead_trigger_after_replies: clamped } }, 'lead_trigger_after_replies');
                    }
                  }}
                  style={{ width: '80px', fontSize: '14px', padding: '8px 12px', border: '1px solid #E2E8F0', borderRadius: '8px', outline: 'none', fontFamily: 'inherit', color: '#0F172A', background: '#fff' }}
                />
              </div>
            </div>

            <OverageSettings domainId={domain.id} domain={domain} onSave={saveSettings} savingField={savingField} />

            {/* カード③: 追加で学習させる */}
            <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
              <div>
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#0F172A', marginBottom: '4px' }}>追加で学習させる</div>
                <div style={{ fontSize: '13px', color: '#64748B' }}>URLの選び直しや追加のほか、PDFやテキストファイルをアップロードして学習させることもできます。</div>
              </div>
              <button
                onClick={() => navigate(getDomainDiscoverPath(domain.id))}
                style={{ background: '#fff', color: '#475569', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}
              >
                学習ページを選ぶ
              </button>
            </div>

            {/* カード: 手動停止/再開 */}
            <div style={{ background: '#fff', border: `1px solid ${domain.status === 'inactive' ? '#FCD34D' : '#E2E8F0'}`, borderRadius: '12px', padding: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
              <div>
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#0F172A', marginBottom: '4px' }}>チャットボットの停止</div>
                {domain.status === 'inactive' ? (
                  <div style={{ fontSize: '13px', color: '#B45309' }}>
                    {domain.deactivated_reason === 'manual' && '手動停止中です。'}
                    {domain.deactivated_reason === 'subscription_expired' && '解約のため停止中です。'}
                    {domain.deactivated_reason === 'trial_expired' && 'トライアル終了のため停止中です。'}
                    {!domain.deactivated_reason && '停止中です。'}
                    {(domain.deactivated_reason === 'manual' || !domain.deactivated_reason) && ' 再開ボタンで即時有効になります。'}
                  </div>
                ) : (
                  <div style={{ fontSize: '13px', color: '#64748B' }}>停止するとチャットボットが利用できなくなります。</div>
                )}
              </div>
              {(domain.status === 'active' || domain.deactivated_reason === 'manual' || !domain.deactivated_reason) && (
                <button
                  data-testid="domain-status-toggle"
                  onClick={() => updateDomain.mutate({ status: domain.status === 'active' ? 'inactive' : 'active' })}
                  disabled={updateDomain.isPending}
                  style={{ background: domain.status === 'active' ? '#fff' : '#0a7a72', color: domain.status === 'active' ? '#475569' : '#fff', border: domain.status === 'active' ? '1px solid #E2E8F0' : 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', flexShrink: 0, opacity: updateDomain.isPending ? 0.6 : 1 }}
                >
                  {domain.status === 'active' ? '停止する' : '再開する'}
                </button>
              )}
            </div>

          </div>
        </TabsContent>

        {/* 分析タブ */}
        <TabsContent value="analytics">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {/* 【1】段階開示バナー */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', background: '#F8FAFC', borderRadius: '10px', border: '1px solid #E2E8F0' }}>
              <div style={{ width: '24px', height: '24px', borderRadius: '50%', border: '1.5px solid #94A3B8', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94A3B8', flexShrink: 0, fontSize: '13px', fontWeight: 700 }}>i</div>
              <span style={{ fontSize: '13px', color: '#94A3B8' }}>くわしい分析は、ボットを使い込むほど見られるようになります。</span>
            </div>

            {/* 【2】stat-card2 × 3 */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
              {/* ①訪問者数 */}
              <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '20px' }}>
                <div style={{ fontSize: '12px', color: '#64748B', marginBottom: '8px' }}>訪問者数</div>
                <div style={{ fontSize: '28px', fontWeight: 700, color: '#0F172A', marginBottom: '6px' }}>
                  {summary?.visitors_count ?? '-'}
                </div>
                <div style={{ fontSize: '12px', color: '#94A3B8' }}>サイトを訪れた人数</div>
              </div>
              {/* ②チャット利用者数 */}
              <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '20px' }}>
                <div style={{ fontSize: '12px', color: '#64748B', marginBottom: '8px' }}>チャット利用者数</div>
                <div style={{ fontSize: '28px', fontWeight: 700, color: '#0F172A', marginBottom: '6px' }}>
                  {summary?.chat_users_count ?? '-'}
                </div>
                <div style={{ fontSize: '12px', color: '#94A3B8' }}>チャットを利用した人数</div>
              </div>
              {/* ③メッセージ数 */}
              <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '20px' }}>
                <div style={{ fontSize: '12px', color: '#64748B', marginBottom: '8px' }}>メッセージ数</div>
                <div style={{ fontSize: '28px', fontWeight: 700, color: '#0F172A', marginBottom: '6px' }}>
                  {summary?.messages_count ?? '-'}
                </div>
                <div style={{ fontSize: '12px', color: '#94A3B8' }}>送受信メッセージ合計</div>
              </div>
            </div>

            {/* 【3】チャット利用者の推移 棒グラフ */}
            <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#0F172A' }}>チャット利用者の推移</div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {([7, 30] as const).map((d) => (
                    <button
                      key={d}
                      onClick={() => setTrendDays(d)}
                      style={{ fontSize: '12px', fontWeight: 700, padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', border: trendDays === d ? '1px solid #0a7a72' : '1px solid #E2E8F0', background: trendDays === d ? '#F0FDFA' : '#fff', color: trendDays === d ? '#0a7a72' : '#64748B' }}
                    >
                      {d}日
                    </button>
                  ))}
                </div>
              </div>
              {(() => {
                const sliced = dailyTrend.slice(-trendDays);
                const maxCount = Math.max(...sliced.map(d => d.session_count), 1);
                const BAR_H = 120;
                return sliced.length > 0 ? (
                  <div className="chart-area" style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: `${BAR_H + 28}px` }}>
                    {sliced.map((d, i) => {
                      const barH = Math.max(Math.round((d.session_count / maxCount) * BAR_H), d.session_count > 0 ? 2 : 0);
                      const label = d.date.slice(5).replace('-', '/');
                      return (
                        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
                          <div className="cbar" style={{ width: '100%', height: `${barH}px`, background: '#0a7a72', borderRadius: '3px 3px 0 0', minHeight: d.session_count > 0 ? '2px' : '0' }} />
                          <div className="clab" style={{ fontSize: '10px', color: '#94A3B8', marginTop: '4px', whiteSpace: 'nowrap' }}>{label}</div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ height: `${BAR_H + 28}px`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#CBD5E1', fontSize: '13px' }}>
                    データがありません
                  </div>
                );
              })()}
            </div>

            {/* 【4】詳細グラフ (pdLevel >= 2) */}
            {pdLevel >= 2 && (
              <>
                <ConversationAnalytics domainId={domain.id} />
                <UnansweredQuestions domainId={domain.id} />
              </>
            )}


          </div>
        </TabsContent>

        {/* 会話履歴タブ */}
        <TabsContent value="history">
          {pdLevel >= 2 && id && (
            <ChatHistoryPage embeddedDomainId={id} />
          )}
        </TabsContent>
      </Tabs>

      <InstallGuideModal
        isOpen={installGuideOpen}
        onClose={() => setInstallGuideOpen(false)}
        embedCode={generateEmbedCode(domain.api_key || domain.api_key_masked || '')}
      />
    </div>
  );
};
